/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useState } from 'react';
import { Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import {
  Alert,
  DashboardDataFilter,
  DeviceAttributesV1,
  Group,
  ParametersType,
  ParticleData,
  PriorityList
} from '../constants/telosair-interfaces';
import { useData } from '../context/data.context';
import MapGroupMarker from './MapGroupMarker';
import DeviceHoverCard from './DeviceHoverCard';

type GroupMapProps = {
  selectedParameters: ParametersType[];
  selectedDevice?: number | null;
  selectedGroup: Group;
  onBack?: () => void;
  shouldHighlight?: boolean;
  selectedGroupName?: string;
  onDashboard?: boolean;
  showExpandedView?: boolean;
  setDataFilter?: (filter: DashboardDataFilter | null) => void;
};

type DeviceHoverState = {
  x: number;
  y: number;
  deviceData: DeviceAttributesV1;
  particleData: ParticleData;
  alert: Alert[];
};

/**
 * A component to display devices of a group on a Google Map.
 * It shows all devices within the group and provides hover information.
 * @param props - Props for the component.
 * @returns A Google Map component with device markers.
 */
export default function GroupMap(props: GroupMapProps): ReactElement {
  const {
    selectedParameters,
    selectedDevice,
    selectedGroup,
    onBack,
    shouldHighlight,
    selectedGroupName,
    onDashboard,
    showExpandedView,
    setDataFilter
  } = props;
  const { loading } = useData();
  const map = useMap();
  const [hovered, setHovered] = useState<DeviceHoverState | null>(null);

  useEffect(() => {
    if (selectedGroup.sensorDetailsList.length > 0 && map) {
      const newBounds = new window.google.maps.LatLngBounds();
      selectedGroup.sensorDetailsList.forEach((device) => {
        newBounds.extend(new window.google.maps.LatLng(device.y, device.x));
      });
      map.fitBounds(newBounds);
    }
  }, [selectedGroup, map]);

  return (
    <div className="w-full h-full flex flex-col">
      {selectedGroupName && (
        <div className={` ${onDashboard && 'py-2'}`}>
          <div
            className={`text-gray-700 dark:text-gray-300 flex items-center font-medium ${
              // eslint-disable-next-line no-nested-ternary
              showExpandedView ? 'text-sm font-medium pt-1' : onDashboard ? 'text-xs' : 'text-base '
            }`}
          >
            {onBack && (
              <button
                onClick={onBack}
                type="button"
                className="material-icons-outlined text-xl ml-[-0.625rem]"
              >
                navigate_before
              </button>
            )}
            <span>{selectedGroupName}</span>
            {onDashboard && (
              <span className={`text-primary-300 ${showExpandedView ? 'pl-3' : 'pl-2'}`}>
                {selectedGroup.sensorDetailsList?.length} Devices{' '}
              </span>
            )}
          </div>
          {!onDashboard && (
            <p className="text-gray-800 dark:text-gray-500 text-sm font-semibold pt-2 pb-3">
              {selectedGroup?.sensorDetailsList?.length} Devices{' '}
              <span className="text-primary-500 font-medium border-l-2 border-l-gray-500 pl-1">
                {
                  selectedGroup?.sensorDetailsList.filter(
                    (currrentDevice) => currrentDevice.serial_number >= 200
                  ).length
                }{' '}
                Parameters Critical
              </span>
            </p>
          )}
        </div>
      )}
      <div className="flex flex-1 rounded-md overflow-hidden">
        <Map
          mapId="2710c5f22fa0158b"
          defaultCenter={{ lat: 0, lng: 0 }} // Default center is arbitrary since bounds will fit
          defaultZoom={15}
          gestureHandling="auto"
          scrollwheel
          draggable
          zoomControl={false}
          disableDefaultUI
          onClick={() => {
            setHovered(null);
          }}
        >
          {selectedGroup.sensorDetailsList.map((device) => (
            <MapGroupMarker
              key={device.id}
              deviceData={device}
              selectedParameters={selectedParameters}
              selectedDevice={selectedDevice}
              setHovered={setHovered}
              shouldHighlight={shouldHighlight}
              setDataFilter={setDataFilter}
            />
          ))}
        </Map>
      </div>
      {hovered && (
        <div className="relative">
          <DeviceHoverCard
            loading={loading}
            deviceData={hovered.deviceData}
            particleData={hovered.particleData}
            alert={hovered.alert}
            selectedParameters={selectedParameters}
            position={hovered}
          />
        </div>
      )}
    </div>
  );
}
